/**
 * SalesAllocationEditSameDoDialog — bulk "Edit (similar DO)" grid for Sales Control ›
 * Sale Allocation. Converted 1:1 from Access `frmCtrlSalesQrySaleAlc_Edit_SameDO`
 * (wrapper) + subform `frmCtrlSalesQrySaleAlc_TickCont` (datasheet grid).
 *
 * Behaviour (Access btnEditMany_Click → form open):
 *   - Open with `doNo` = the selected list row's delivery_order. Empty doNo →
 *     parent must warn "Do no is empty!" before opening; this dialog also guards.
 *   - Load every allocation that shares that delivery_order into a grid, each row
 *     pre-ticked (checked_row = 1).
 *   - "Tick All" header checkbox toggles every row (Access tick_all_Click).
 *   - Only three columns are editable per row (Access labels carry "(EDIT)"):
 *       Receipt No · Alc Bag · Alc Quantity  (UPDATE target columns).
 *   - btnSave loops the TICKED rows and UPDATEs receipt_no / allocating_bag /
 *     allocating_quantity for each (is_status = 0). Unticked rows are skipped.
 *
 * The other columns (ID, M. Truck No, Container No., M. Date, M. Net weight,
 * Contract No, DO, Booking No., Contract Quanlity, From WH) are display-only context —
 * read-only in the grid exactly like the Access datasheet (no Enabled/Locked override).
 *
 * BE NOT BUILT YET: hooks come from `saleAllocationEditSameDoApi` (GET same-do /
 * PUT same-do, both TODO). Until shipped the grid shows an empty state, no fake rows.
 */
import { useEffect, useMemo, useState } from 'react'
import { HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '../../theme/responsive'
import {
  Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, Table, TableBody, TableCell, TableFooter, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'

import {
  type SaleAllocationSameDoRow,
  type SaleAllocationSameDoUpdateItem,
  type SaleAlcLookupItem,
  useGetSaleAllocationsSameDoQuery,
  useUpdateSaleAllocationsSameDoMutation,
  useSaleAlcLookupReceiptNosQuery,
  useSaleAlcLookupVirtualWarehousesQuery,
} from '../../store/api/saleControlApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import { DISPLAY_DATE_FORMAT } from '../../utils/date'
import dayjs from 'dayjs'

interface Props {
  open: boolean
  /** The selected list row's delivery_order — every allocation sharing it is loaded. */
  doNo: string
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

/** Per-row editable draft + tick state, keyed by salesAllocationScsId. */
interface RowDraft {
  checked: boolean
  receiptNo: string
  allocatingBag: string
  allocatingQuantity: string
}

const SM = 'small' as const
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
// Style bảng dùng chung với Form "Edit (Booking) — Sales Allocation".
const roCellSx: SxProps<Theme> = { fontSize: '0.78rem', whiteSpace: 'nowrap', color: 'text.primary', py: 0.5 }
// Edit-cell input (outlined box, minHeight 30, 0.75rem; px:1 để text không bị hộp che).
const editFieldSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 30, fontSize: '0.75rem' },
  '& .MuiInputBase-input': { py: 0.4, px: 1, fontSize: '0.75rem' },
}
// Body cell của cột Edit — giảm padding ngang để ô outlined rộng hơn.
const editCellSx = (w: number): SxProps<Theme> => ({ minWidth: w, px: 0.75 })
// Receipt No combo — như editFieldSx nhưng ép text căn trái (align left).
const receiptInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 30, fontSize: '0.75rem' },
  '& .MuiInputBase-input': { py: 0.4, px: 1, fontSize: '0.75rem', textAlign: 'left' },
}
// Header cell: khoá width để tên cột 1 dòng (KHÔNG wrap).
const hCell = (w: number): SxProps<Theme> => ({ width: w, minWidth: w, whiteSpace: 'nowrap' })
// Cột Tick sticky trái #1; cột ID sticky-col #2 offset đúng bề rộng cột tick.
const TICK_W = 48
const tickCellSx: SxProps<Theme> = { width: TICK_W, minWidth: TICK_W, p: 0, textAlign: 'center' }
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }
// Footer Total — sticky đáy, nền opaque, viền trên; override màu chữ MuiTableCell-footer.
const footCellSx: SxProps<Theme> = {
  fontWeight: 800, whiteSpace: 'nowrap', py: 0.6, color: 'text.primary',
  bgcolor: 'background.paper', borderTop: (t: Theme) => `2px solid ${t.palette.divider}`,
  position: 'sticky', bottom: 0, zIndex: 3,
}
const footCornerSx: SxProps<Theme> = { ...footCellSx, zIndex: 4 }

function parseNum(v: string): number | null {
  const t = (v ?? '').toString().replace(/,/g, '').trim()
  if (!t) return null
  const n = parseFloat(t)
  return Number.isFinite(n) ? n : null
}
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const s = (v: unknown): string => (v == null ? '' : String(v))
const fmtDate = (v: string | null): string => (v ? dayjs(v).format(DISPLAY_DATE_FORMAT) : '')
// Combobox flat "<CODE> — <NAME>" (collapse khi code == label), giống Form New/Edit/Copy.
const fullLabel = (o: SaleAlcLookupItem): string =>
  (o.value === o.label ? String(o.label ?? '') : `${o.value} — ${o.label}`)

/** receipt_no có thể là Receipt No (GDN) hoặc Virtual WH (RDN) — như 2 combo bên
 *  Form New. Gộp 2 nguồn vào 1 list, mỗi item mang tag `kind` để phân biệt. */
type ReceiptOption = SaleAlcLookupItem & { kind: 'GDN' | 'RDN' }
const KIND_LABEL: Record<ReceiptOption['kind'], string> = { GDN: 'Receipt', RDN: 'Virtual WH' }

/** Receipt No / Virtual WH picklist trong ô Edit — cùng danh sách với Form
 *  New/Edit/Copy. Input hiển thị code; dropdown gắn tag Receipt/Virtual WH. */
function ReceiptCombo(props: {
  value: string
  options: ReceiptOption[]
  loading?: boolean
  disabled?: boolean
  onPick: (value: string) => void
}) {
  const { value, options, loading, disabled, onPick } = props
  const sel: ReceiptOption | null = value ? (options.find((o) => o.value === value) ?? { value, label: value, kind: 'GDN' }) : null
  const merged = sel && !options.some((o) => o.value === sel.value && o.kind === sel.kind) ? [sel, ...options] : options
  const seen = new Set<string>()
  const opts = merged.filter((o) => { const k = `${o.kind}:${o.value}`; if (seen.has(k)) return false; seen.add(k); return true })
  return (
    <Autocomplete
      size={SM} fullWidth disabled={disabled}
      options={opts} value={sel} loading={loading}
      onChange={(_e, v) => onPick(v?.value ?? '')}
      getOptionLabel={(o) => (!o ? '' : o.value)}
      renderOption={(liProps, o) => (
        <li {...liProps} key={`${o.kind}:${o.value}`} style={{ whiteSpace: 'nowrap', display: 'flex', gap: 8, alignItems: 'center' }}>
          <Box component="span" sx={{
            flexShrink: 0, fontSize: '0.62rem', fontWeight: 700, lineHeight: 1.6, px: 0.7, borderRadius: 0.75,
            color: '#fff', bgcolor: o.kind === 'GDN' ? 'primary.main' : 'secondary.main',
          }}>{KIND_LABEL[o.kind]}</Box>
          <span>{fullLabel(o)}</span>
          {o.meta?.truck ? (
            <Box component="span" sx={{ flexShrink: 0, color: 'text.secondary', fontSize: '0.72rem' }}>
              🚚 {o.meta.truck}
            </Box>
          ) : null}
        </li>
      )}
      isOptionEqualToValue={(a, b) => a.value === b.value && a.kind === b.kind}
      // Search theo cả label/tag/biển số xe hiện trong dropdown — getOptionLabel
      // chỉ trả code (anti-pattern #55 /convert-form).
      filterOptions={(list, state) => {
        const q = state.inputValue.trim().toLowerCase()
        if (!q) return list
        return list.filter((o) =>
          `${fullLabel(o)} ${KIND_LABEL[o.kind]} ${o.meta?.truck ?? ''}`.toLowerCase().includes(q))
      }}
      // Dropdown canh mép TRÁI của ô (bottom-start) + rộng hơn ô qua paper minWidth
      // (KHÔNG dùng width:fit-content vì nó phá canh trái → list bị lệch/giữa).
      slotProps={{ popper: { placement: 'bottom-start' }, paper: { sx: { minWidth: 360, maxWidth: 560 } } }}
      renderInput={(params) => <TextField {...params} sx={receiptInputSx} />}
    />
  )
}

export default function SalesAllocationEditSameDoDialog({ open, doNo, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [drafts, setDrafts] = useState<Record<number, RowDraft>>({})

  const { data, isFetching, isError } = useGetSaleAllocationsSameDoQuery(doNo, { skip: !open || !doNo })
  const rows: SaleAllocationSameDoRow[] = useMemo(() => data?.rows ?? [], [data])

  // Receipt No dropdown — gộp 2 nguồn Receipt No (GDN) + Virtual WH (RDN) như Form New,
  // và LỌC THEO doNo (mọi dòng trong dialog cùng 1 delivery_order = doNo).
  const receiptNos = useSaleAlcLookupReceiptNosQuery({ doNo: doNo || undefined }, { skip: !open || !doNo })
  const virtualWhs = useSaleAlcLookupVirtualWarehousesQuery({ doNo: doNo || undefined }, { skip: !open || !doNo })
  const receiptOptions = useMemo<ReceiptOption[]>(() => [
    ...(receiptNos.data?.items ?? []).map((o) => ({ ...o, kind: 'GDN' as const })),
    ...(virtualWhs.data?.items ?? []).map((o) => ({ ...o, kind: 'RDN' as const })),
  ], [receiptNos.data, virtualWhs.data])
  const receiptLoading = receiptNos.isFetching || virtualWhs.isFetching

  const [updateRows, { isLoading: saving }] = useUpdateSaleAllocationsSameDoMutation()

  // Hydrate drafts when rows arrive. Access pre-ticks every row (checked_row = 1)
  // and pre-formats the three editable number/text fields to Standard.
  useEffect(() => {
    if (!open) return
    const next: Record<number, RowDraft> = {}
    for (const r of rows) {
      next[r.salesAllocationScsId] = {
        checked: true,
        receiptNo: s(r.receiptNo),
        allocatingBag: fmtStd(r.allocatingBag),
        allocatingQuantity: fmtStd(r.allocatingQuantity),
      }
    }
    setDrafts(next)
  }, [open, rows])

  const setRow = (id: number, patch: Partial<RowDraft>) =>
    setDrafts((p) => ({ ...p, [id]: { ...p[id], ...patch } }))

  const allChecked = rows.length > 0 && rows.every((r) => drafts[r.salesAllocationScsId]?.checked)
  const someChecked = rows.some((r) => drafts[r.salesAllocationScsId]?.checked)
  const checkedCount = rows.filter((r) => drafts[r.salesAllocationScsId]?.checked).length

  // Total row — sum 2 cột số editable của các dòng ĐƯỢC TICK (= sẽ được Save),
  // cập nhật live khi user gõ (bổ sung tiện ích, giống Form Edit (Booking)).
  const totals = useMemo(() => {
    let bag = 0, qty = 0
    for (const r of rows) {
      const d = drafts[r.salesAllocationScsId]
      if (!d?.checked) continue
      bag += parseNum(d.allocatingBag) ?? 0
      qty += parseNum(d.allocatingQuantity) ?? 0
    }
    return { bag, qty }
  }, [rows, drafts])

  // Access tick_all_Click — toggle every row on/off.
  const toggleAll = (checked: boolean) =>
    setDrafts((p) => {
      const next = { ...p }
      for (const r of rows) next[r.salesAllocationScsId] = { ...next[r.salesAllocationScsId], checked }
      return next
    })

  const canSave = canEdit && !saving && checkedCount > 0

  const handleClose = () => { if (!saving) onClose() }

  // Access btnSave_Click — UPDATE each ticked row's three editable columns.
  const handleSave = async () => {
    if (!canSave) return
    const payload: SaleAllocationSameDoUpdateItem[] = rows
      .filter((r) => drafts[r.salesAllocationScsId]?.checked)
      .map((r) => {
        const d = drafts[r.salesAllocationScsId]
        return {
          salesAllocationScsId: r.salesAllocationScsId,
          receiptNo: d.receiptNo.trim() || null,
          allocatingBag: parseNum(d.allocatingBag),
          allocatingQuantity: parseNum(d.allocatingQuantity),
        }
      })
    try {
      await updateRows({ rows: payload }).unwrap()
      snack.success('Successfully updated')
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Update failed'))
    }
  }

  // Display-only context columns, in the Access TickCont datasheet order
  // (authoritative ColumnOrder: m_truck_no → container_no → m_date → …).
  const displayCols: { key: keyof SaleAllocationSameDoRow; label: string; w: number; num?: boolean; date?: boolean }[] = [
    { key: 'mTruckNo', label: 'M. Truck No', w: 120 },
    { key: 'containerNo', label: 'Container No.', w: 150 },
    { key: 'mDate', label: 'M. Date', w: 120, date: true },
    { key: 'mNetWeight', label: 'M. Net weight', w: 130, num: true },
    { key: 'saleContract', label: 'Contract No', w: 110 },
    { key: 'deliveryOrder', label: 'DO', w: 110 },
    { key: 'bookingNo', label: 'Booking No.', w: 160 },
    { key: 'contractQuality', label: 'Contract Quanlity', w: 150 },
    { key: 'fromWarehouse', label: 'From WH', w: 140 },
  ]

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { maxHeight: 'min(840px, 94vh)' } }}
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" noWrap title={`Edit - Similar DO${doNo ? ` — ${doNo}` : ''}`} sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700 }}>
          Edit - Similar DO{doNo ? ` — ${doNo}` : ''}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', ...HIDE_ON_MOBILE_INLINE, flexShrink: 0 }}>(Only edit Ticked Rows)</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit allocations.</Alert>}
        {!doNo && <Alert severity="warning" sx={{ mb: 1.5 }}>Do no is empty!</Alert>}
        {isError && <Alert severity="error" sx={{ mb: 1.5 }}>Could not load allocations for this DO.</Alert>}

        {isFetching ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>
            No allocations found for this delivery order.
          </Typography>
        ) : (
          <Box className="app-dataframe app-dataframe--compact">
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', maxHeight: '70vh' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 1812 }}>
                <TableHead>
                  <TableRow>
                    <TableCell className="app-table-header-cell app-sticky-col" sx={tickCellSx}>
                      <Checkbox
                        size={SM} sx={{ p: 0.25 }}
                        checked={allChecked}
                        indeterminate={!allChecked && someChecked}
                        onChange={(e) => toggleAll(e.target.checked)}
                        disabled={!canEdit}
                      />
                    </TableCell>
                    <TableCell className="app-table-header-cell app-sticky-col-2" style={{ left: TICK_W }} sx={hCell(80)}>ID</TableCell>
                    <TableCell className="app-table-header-cell" sx={hCell(230)}>Receipt No.</TableCell>
                    <TableCell className="app-table-header-cell" align="right" sx={hCell(130)}>Alc Bag</TableCell>
                    <TableCell className="app-table-header-cell" align="right" sx={hCell(150)}>Alc Quantity</TableCell>
                    {displayCols.map((c) => (
                      <TableCell key={c.key} className="app-table-header-cell" sx={hCell(c.w)} align={c.num ? 'right' : 'left'}>{c.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const d = drafts[r.salesAllocationScsId]
                    if (!d) return null
                    // Dòng chưa tick → khoá readonly các ô editable (chỉ Edit dòng đã chọn).
                    const cellDisabled = !canEdit || !d.checked
                    return (
                      <TableRow key={r.salesAllocationScsId} hover>
                        <TableCell className="app-sticky-col" sx={tickCellSx}>
                          <Checkbox
                            size={SM} sx={{ p: 0.25 }}
                            checked={d.checked}
                            onChange={(e) => setRow(r.salesAllocationScsId, { checked: e.target.checked })}
                            disabled={!canEdit}
                          />
                        </TableCell>
                        <TableCell className="app-sticky-col-2" style={{ left: TICK_W }} sx={roCellSx}>{s(r.salesAllocationScsId)}</TableCell>
                        <TableCell sx={editCellSx(230)}>
                          <ReceiptCombo
                            value={d.receiptNo}
                            options={receiptOptions}
                            loading={receiptLoading}
                            disabled={cellDisabled}
                            onPick={(v) => setRow(r.salesAllocationScsId, { receiptNo: v })}
                          />
                        </TableCell>
                        <TableCell sx={editCellSx(130)}>
                          <TextField
                            size={SM} fullWidth type="text" value={d.allocatingBag} disabled={cellDisabled}
                            sx={editFieldSx} inputProps={numProps}
                            onChange={(e) => setRow(r.salesAllocationScsId, { allocatingBag: e.target.value })}
                            onBlur={() => setRow(r.salesAllocationScsId, { allocatingBag: fmtStd(d.allocatingBag) })}
                          />
                        </TableCell>
                        <TableCell sx={editCellSx(150)}>
                          <TextField
                            size={SM} fullWidth type="text" value={d.allocatingQuantity} disabled={cellDisabled}
                            sx={editFieldSx} inputProps={numProps}
                            onChange={(e) => setRow(r.salesAllocationScsId, { allocatingQuantity: e.target.value })}
                            onBlur={() => setRow(r.salesAllocationScsId, { allocatingQuantity: fmtStd(d.allocatingQuantity) })}
                          />
                        </TableCell>
                        {displayCols.map((c) => {
                          const raw = r[c.key]
                          const text = c.date ? fmtDate(raw as string | null) : c.num ? fmtStd(raw) : s(raw)
                          return (
                            <TableCell key={c.key} sx={roCellSx} align={c.num ? 'right' : 'left'}>{text}</TableCell>
                          )
                        })}
                      </TableRow>
                    )
                  })}
                </TableBody>
                {rows.length > 0 && (
                  <TableFooter>
                    <TableRow>
                      <TableCell className="app-sticky-col" sx={footCornerSx} />
                      <TableCell className="app-sticky-col-2" style={{ left: TICK_W }} sx={footCornerSx}>Total</TableCell>
                      <TableCell sx={footCellSx} />
                      <TableCell sx={footCellSx} align="right">{fmtStd(totals.bag)}</TableCell>
                      <TableCell sx={footCellSx} align="right">{fmtStd(totals.qty)}</TableCell>
                      {displayCols.map((c) => (
                        <TableCell key={c.key} sx={footCellSx} />
                      ))}
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
